import { jsxs, jsx } from "react/jsx-runtime";
import { useTranslation } from "react-i18next";
import { cn } from "../../../lib/utils.js";
import "../shared/quiz-blank.js";
import { KatexText } from "../shared/katex-text.js";
import { QuizResultFeedback } from "../shared/quiz-result-feedback.js";
import "../shared/quiz-style-context.js";
import "react";
import { SubmitButton } from "../shared/submit-button.js";
import { QuizImage } from "../shared/quiz-image.js";
import "../../../node_modules/.pnpm/hls.js@1.6.15/node_modules/hls.js/dist/hls.js";
import "../../../context/DevToolsContext.js";
import { QuizHeader } from "../shared/quiz-header.js";
import { QuizSolveDescription } from "../shared/quiz-solve-description.js";
import { QUIZ_OPTION_STATES } from "../shared/quiz-styles.js";
import { formatText } from "../../../utils/quiz-parser.js";
import { useInlineQuiz } from "../hooks/use-inline-quiz.js";
import Check from "../../../node_modules/.pnpm/lucide-react@0.309.0_react@18.3.1/node_modules/lucide-react/dist/esm/icons/check.js";
import X from "../../../node_modules/.pnpm/lucide-react@0.309.0_react@18.3.1/node_modules/lucide-react/dist/esm/icons/x.js";
function InlineAdult({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  initialAnswers,
  submitLabel,
  hideSubmitButton = false,
  onAnswerChange,
  disableSound = false,
  difficultyLevel,
  hideExplanation = false
}) {
  const { t } = useTranslation("quiz");
  const {
    children,
    selectedKeysByChild,
    isSolved,
    canSubmit,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    resolvedCorrectKeySetByChild,
    perChildCorrectsBySelectedKey,
    totalCorrectCount,
    totalChildCount,
    handleSelectChild,
    handleSubmit
  } = useInlineQuiz({
    quizId,
    quiz,
    onSolve,
    solveResult,
    showReallyTrue,
    isViewSolved,
    initialAnswers,
    onAnswerChange
  });
  return /* @__PURE__ */ jsxs("div", { className: "space-y-6", children: [
    /* @__PURE__ */ jsx(
      QuizHeader,
      {
        quizId,
        typeLabel: t("types.inline.label"),
        title: quiz.title,
        difficultyLevel,
        image: quiz.image,
        imageMobile: quiz.imageMobile,
        desmos: quiz.desmos,
        vars: quiz.vars,
        problem: quiz.problem && /* @__PURE__ */ jsx("div", { className: "text-xl leading-relaxed text-gray-900", children: formatText(quiz.problem, `${quizId}-problem`, quiz.additionalImages ?? void 0) })
      }
    ),
    children.map((child, childIdx) => /* @__PURE__ */ jsx(
      InlineChildBlock,
      {
        child,
        childIdx,
        selectedKeys: selectedKeysByChild[childIdx] ?? [],
        isSolved,
        isViewSolved,
        shouldShowCorrectAnswers,
        resolvedCorrectKeySet: resolvedCorrectKeySetByChild[childIdx] ?? /* @__PURE__ */ new Set(),
        correctsBySelectedKey: perChildCorrectsBySelectedKey[childIdx] ?? /* @__PURE__ */ new Map(),
        onSelect: (key) => handleSelectChild(childIdx, key),
        t
      },
      child.scriptId || childIdx
    )),
    !isSolved && !isViewSolved && !hideSubmitButton && /* @__PURE__ */ jsx(SubmitButton, { onClick: handleSubmit, disabled: !canSubmit, label: submitLabel }),
    isSolved && /* @__PURE__ */ jsx(
      QuizResultFeedback,
      {
        isCorrect: (solveResult == null ? void 0 : solveResult.correct) ?? false,
        show: isSolved,
        correctAnswer: shouldShowCorrectAnswers ? t("types.inline.partialResult", { correct: totalCorrectCount, total: totalChildCount }) : void 0,
        disableSound,
        isViewSolved
      }
    ),
    isSolved && !hideExplanation && /* @__PURE__ */ jsx(
      QuizSolveDescription,
      {
        description: (solveResult == null ? void 0 : solveResult.description) ?? void 0,
        commentaryImages: quiz.commentaryImages,
        vars: quiz.vars,
        id: `${quizId}-desc`,
        actionVisible: !(solveResult == null ? void 0 : solveResult.correct),
        actionLabel: isShowingCorrectAnswers ? t("common.viewMyAnswer") : t("common.viewCorrectAnswer"),
        onActionClick: () => setIsShowingCorrectAnswers((v) => !v)
      }
    )
  ] });
}
function InlineChildBlock({
  child,
  childIdx,
  selectedKeys,
  isSolved,
  isViewSolved,
  shouldShowCorrectAnswers,
  resolvedCorrectKeySet,
  correctsBySelectedKey,
  onSelect,
  t
}) {
  const choices = child.random && Array.isArray(child.random) && child.random.length > 0 ? child.random.map((i) => child.choices[i]).filter(Boolean) : child.choices;
  return /* @__PURE__ */ jsxs("div", { className: "space-y-2", children: [
    /* @__PURE__ */ jsx("div", { className: "text-sm font-bold text-gray-500", children: t("types.inline.childLabel", { n: childIdx + 1 }) }),
    child.problem && /* @__PURE__ */ jsx("div", { className: "text-lg leading-relaxed text-gray-900", children: /* @__PURE__ */ jsx(KatexText, { text: child.problem }) }),
    /* @__PURE__ */ jsx("div", { className: "flex flex-wrap gap-3", role: "listbox", "aria-multiselectable": "true", children: choices.map((choice, i) => {
      const isSelected = selectedKeys.includes(choice.key);
      const isInResolvedCorrect = resolvedCorrectKeySet.has(choice.key);
      const isCorrectByCorrects = correctsBySelectedKey.get(choice.key);
      const showAsCorrect = isSolved && (shouldShowCorrectAnswers ? isInResolvedCorrect : isSelected && (isCorrectByCorrects !== void 0 ? isCorrectByCorrects : isInResolvedCorrect));
      const isWrongSelected = isSolved && !shouldShowCorrectAnswers && isSelected && !showAsCorrect;
      return /* @__PURE__ */ jsxs(
        "button",
        {
          role: "option",
          "aria-selected": isSelected,
          onClick: () => onSelect(choice.key),
          disabled: isSolved || isViewSolved,
          className: cn(
            "inline-flex items-center gap-2 px-4 py-2 rounded-lg border-2 text-base font-medium transition-all",
            !isSolved && !isSelected && QUIZ_OPTION_STATES.default,
            !isSolved && !isSelected && "hover:border-blue-400 hover:bg-blue-50",
            !isSolved && isSelected && QUIZ_OPTION_STATES.selected,
            showAsCorrect && QUIZ_OPTION_STATES.correct,
            isWrongSelected && QUIZ_OPTION_STATES.wrong,
            isSolved && !showAsCorrect && !isWrongSelected && QUIZ_OPTION_STATES.disabled,
            (isSolved || isViewSolved) && "cursor-not-allowed"
          ),
          children: [
            /* @__PURE__ */ jsx(
              "span",
              {
                className: cn(
                  "w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0",
                  !isSelected && !isSolved && "bg-gray-200 text-gray-600",
                  isSelected && !isSolved && "bg-blue-500 text-white",
                  showAsCorrect && "bg-green-500 text-white",
                  isWrongSelected && "bg-red-500 text-white",
                  isSolved && !showAsCorrect && !isWrongSelected && "bg-gray-100 text-gray-400"
                ),
                children: showAsCorrect ? /* @__PURE__ */ jsx(Check, { className: "w-3 h-3" }) : isWrongSelected ? /* @__PURE__ */ jsx(X, { className: "w-3 h-3" }) : i + 1
              }
            ),
            choice.image && /* @__PURE__ */ jsx(
              QuizImage,
              {
                image: choice.image,
                alt: t("types.multiChoice.optionAlt", { number: i + 1 }),
                className: "my-0 shrink-0"
              }
            ),
            choice.answer && /* @__PURE__ */ jsx(KatexText, { text: choice.answer })
          ]
        },
        choice.key
      );
    }) })
  ] });
}
export {
  InlineAdult
};
//# sourceMappingURL=inline-adult.js.map
